import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Phone,
  AlertCircle,
  Clock,
  CheckCircle,
  CalendarClock,
  Plus
} from 'lucide-react';
import { Card } from '@/components/common/Card';
import { Button } from '@/components/common/Button';
import { Badge } from '@/components/common/Badge';
import { useToast } from '@/components/common/Toast';
import CallViewHeader from './CallViewHeader';
import CallStatsCards from './CallStatsCards';

import { CALL_TYPE_CONFIG, STATUS_CONFIG } from '@/theme/corporateTheme';

interface CallEvent {
  id: string;
  contactId: string;
  callType: 'CALL1' | 'CALL2' | 'CALL3';
  dueDate: string;
  status: 'OPEN' | 'DONE' | 'SKIPPED';
  contact?: {
    firstName: string;
    lastName: string;
    account?: {
      name: string;
    };
  };
  assignedTo?: {
    name: string;
  };
  callPackData?: {
    notes?: string;
    duration?: number;
  };
}

type BoardColumn = 'overdue' | 'today' | 'upcoming' | 'completed';

export default function CallBoardView() {
  const navigate = useNavigate();
  const { addToast } = useToast();
  
  const [calls, setCalls] = useState<CallEvent[]>([]);
  const [loading, setLoading] = useState(true);
  const [draggedCall, setDraggedCall] = useState<CallEvent | null>(null);

  useEffect(() => {
    loadCalls();
  }, []);

  const loadCalls = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${import.meta.env.VITE_API_URL ?? ''}/api/call-tasks`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('accessToken')}`,
        },
      });

      if (response.ok) {
        const result = await response.json();
        setCalls(result.data || []);
      }
    } catch (error) {
      console.error('Error loading calls:', error);
      addToast({
        type: 'error',
        title: 'Error',
        description: 'Failed to load calls',
      });
    } finally {
      setLoading(false);
    }
  };

  // Categorize calls into columns
  const categorizeCall = (call: CallEvent): BoardColumn => {
    if (call.status === 'DONE') return 'completed';
    
    const dueDate = new Date(call.dueDate);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const tomorrow = new Date(today);
    tomorrow.setDate(tomorrow.getDate() + 1);
    const nextWeek = new Date(today);
    nextWeek.setDate(nextWeek.getDate() + 7);

    // Check if overdue
    if (dueDate < today) return 'overdue';
    
    // Check if today
    if (dueDate >= today && dueDate < tomorrow) return 'today';
    
    // Check if upcoming (next 7 days)
    if (dueDate >= tomorrow && dueDate < nextWeek) return 'upcoming';
    
    // Everything else goes to upcoming
    return 'upcoming';
  };

  const getColumnCalls = (column: BoardColumn) => {
    return calls.filter(call => categorizeCall(call) === column);
  };

  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getDaysOverdue = (dueDate: string) => {
    const due = new Date(dueDate);
    const today = new Date();
    const diff = Math.floor((today.getTime() - due.getTime()) / (1000 * 60 * 60 * 24));
    return diff;
  };

  // Drag and drop handlers
  const handleDragStart = (call: CallEvent) => {
    setDraggedCall(call);
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
  };

  const handleDrop = async (targetColumn: BoardColumn) => {
    if (!draggedCall) return;

    // Determine what action to take based on target column
    if (targetColumn === 'completed') {
      // Mark as complete
      await updateCallStatus(draggedCall.id, 'DONE');
    } else {
      // For other columns, we'd need to reschedule
      // For now, just show a toast
      addToast({
        type: 'info',
        title: 'Reschedule Required',
        description: 'Please use the detail page to reschedule this call',
      });
    }

    setDraggedCall(null);
  };

  const updateCallStatus = async (callId: string, status: 'DONE') => {
    try {
      const response = await fetch(`${import.meta.env.VITE_API_URL ?? ''}/api/call-tasks/${callId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('accessToken')}`,
        },
        body: JSON.stringify({ status }),
      });

      if (response.ok) {
        addToast({
          type: 'success',
          title: 'Success',
          description: 'Call marked as completed',
        });
        loadCalls(); // Reload calls
      }
    } catch (error) {
      console.error('Error updating call:', error);
      addToast({
        type: 'error',
        title: 'Error',
        description: 'Failed to update call',
      });
    }
  };

  const renderCallCard = (call: CallEvent) => (
    <Card
      key={call.id}
      draggable
      onDragStart={() => handleDragStart(call)}
      onClick={() => navigate(`/calls/${call.id}`)}
      className="p-3 mb-2 cursor-pointer hover:shadow-md transition-shadow bg-white"
    >
      <div className="space-y-2">
        {/* Contact Name & Type */}
        <div className="flex items-start justify-between">
          <div className="flex-1">
            <div className="font-semibold text-sm">
              {call.contact?.firstName} {call.contact?.lastName}
            </div>
            <div className="text-xs text-gray-500 truncate">
              {call.contact?.account?.name}
            </div>
          </div>
          <span className="text-lg">{CALL_TYPE_CONFIG[call.callType].icon}</span>
        </div>

        {/* Call Type Badge */}
        <Badge className={`${CALL_TYPE_CONFIG[call.callType].color} text-xs`}>
          {CALL_TYPE_CONFIG[call.callType].label}
        </Badge>

        {/* Due Date */}
        <div className="flex items-center text-xs text-gray-600">
          <Clock className="w-3 h-3 mr-1" />
          {formatDate(call.dueDate)}
        </div>

        {/* Duration */}
        {call.callPackData?.duration && (
          <div className="text-xs text-gray-500">
            {call.callPackData.duration} minutes
          </div>
        )}

        {/* Overdue Badge */}
        {categorizeCall(call) === 'overdue' && (
          <Badge className="bg-red-100 text-red-700 text-xs">
            {getDaysOverdue(call.dueDate)} days overdue
          </Badge>
        )}
      </div>
    </Card>
  );

  const renderColumn = (
    column: BoardColumn,
    title: string,
    icon: React.ReactNode,
    bgColor: string
  ) => {
    const columnCalls = getColumnCalls(column);

    return (
      <div className="flex-1 min-w-[280px]">
        <Card className={`${bgColor} border-t-4 h-full`}>
          {/* Column Header */}
          <div className="p-4 border-b bg-white">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                {icon}
                <h3 className="font-semibold">{title}</h3>
              </div>
              <Badge variant="outline">{columnCalls.length}</Badge>
            </div>
          </div>

          {/* Column Content */}
          <div
            className="p-4 space-y-2 min-h-[400px] max-h-[calc(100vh-300px)] overflow-y-auto"
            onDragOver={handleDragOver}
            onDrop={() => handleDrop(column)}
          >
            {columnCalls.length === 0 ? (
              <div className="text-center text-gray-400 text-sm py-8">
                No calls
              </div>
            ) : (
              columnCalls.map(renderCallCard)
            )}
          </div>
        </Card>
      </div>
    );
  };

  // Calculate stats
  const stats = {
    total: calls.length,
    scheduled: calls.filter(c => c.status === 'OPEN').length,
    completed: calls.filter(c => c.status === 'DONE').length,
    overdue: getColumnCalls('overdue').length,
    dueToday: getColumnCalls('today').length,
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-center">
          <Phone className="w-12 h-12 mx-auto mb-4 text-gray-400 animate-pulse" />
          <p className="text-gray-500">Loading board...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Unified Header */}
      <CallViewHeader
        currentView="board"
        title="Call Management"
        subtitle="Kanban board with drag & drop workflow"
        showCreateButton={false}
      />

      {/* Unified Stats */}
      <CallStatsCards stats={stats} />

      {/* Kanban Board */}
      <div className="flex gap-4 overflow-x-auto pb-4">
        {renderColumn(
          'overdue',
          'Overdue',
          <AlertCircle className="w-5 h-5 text-red-700" />,
          'border-red-600'
        )}
        {renderColumn(
          'today',
          'Today',
          <CalendarClock className="w-5 h-5 text-blue-700" />,
          'border-blue-600'
        )}
        {renderColumn(
          'upcoming',
          'Upcoming (Next 7 Days)',
          <Clock className="w-5 h-5 text-indigo-700" />,
          'border-indigo-600'
        )}
        {renderColumn(
          'completed',
          'Completed',
          <CheckCircle className="w-5 h-5 text-emerald-700" />,
          'border-emerald-600'
        )}
      </div>

      {/* Drag & Drop Hint */}
      <Card className="p-4 bg-blue-50 border-blue-200">
        <div className="flex items-start gap-3">
          <Phone className="w-5 h-5 text-blue-600 mt-0.5" />
          <div>
            <p className="font-semibold text-blue-900">Drag & Drop to Update</p>
            <p className="text-sm text-blue-700">
              Drag calls to the "Completed" column to mark them as done. Click any card to view details and reschedule.
            </p>
          </div>
        </div>
      </Card>
    </div>
  );
}
